import compact from "lodash/compact";
import find from "lodash/find";
import keys from "lodash/keys";
import map from "lodash/map";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Stack } from "PFComponents/containers";
import { Modal } from "PFComponents/modal";
import { Typography } from "PFComponents/typography";
import { getProfileName } from "PFCore/helpers/profile";
import { useProfilesBookings } from "PFCore/hooks/queries/profile";
import { useProfilesPlaceholdersDelete } from "PFCore/hooks/queries/profile/use_profiles_placeholders_delete";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useProfilesPageContext } from "../../context";
import css from "./bulk_select_overlay.module.scss";

type DeletePlaceholderProfilesModalProps = {
  onClose: () => void;
};

export const DeletePlaceholderProfilesModal = ({ onClose }: DeletePlaceholderProfilesModalProps) => {
  const { t } = useTranslation("profiles", { keyPrefix: "list.placeholderProfiles" });
  const { showGrowl } = useGrowlStoreActions();

  const { profiles, selectedIds, setSelectedIds } = useProfilesPageContext();

  const { mutate: deletePlaceholders } = useProfilesPlaceholdersDelete();
  const { bookingsByProfileId, isFetching } = useProfilesBookings({
    profiles: map(keys(selectedIds), (id) => ({ id: Number(id), rowKey: id }))
  });

  const handleDeleteProfiles = () => {
    deletePlaceholders(map(keys(selectedIds), Number), {
      onSuccess: () => {
        showGrowl({
          message: t("successMessage")
        });
        setSelectedIds({});
      },
      onError: () => {
        showGrowl({
          message: t("errorMessage"),
          kind: "error"
        });
      }
    });
  };

  const bookingsInfoLabel = useMemo(
    () =>
      compact(
        map(bookingsByProfileId, (bookings, profileId) => {
          const { profile } = find(profiles, ["id", Number(profileId)]) || {};
          return profile && bookings.length > 0 && t("hasBookings", { profileName: getProfileName(profile) });
        })
      ),

    [t, bookingsByProfileId, profiles]
  );

  const bookingsDeleteDisclaimer = useMemo(
    () => (bookingsInfoLabel.length ? t("bookingsWillBeDeleted") : ""),
    [t, bookingsInfoLabel.length]
  );

  return (
    <Modal
      title={t("modalTitle")}
      onOK={handleDeleteProfiles}
      labelOK={t("okLabel")}
      kindOK="danger"
      disableOKButton={isFetching}
      onClose={onClose}
    >
      <Stack>
        {map(bookingsInfoLabel.length ? bookingsInfoLabel : [t("noBookings")], (bookingsInfo, index) => (
          <Typography key={index} variant="bodyRegular">
            {bookingsInfo}
          </Typography>
        ))}
        <Typography variant="bodyRegular">
          {t("modalContent")} {bookingsDeleteDisclaimer}
        </Typography>
        <Typography className={css.modalDisclaimer} variant="labelRegular">
          {t("modalDisclaimer")}
        </Typography>
      </Stack>
    </Modal>
  );
};
